import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import MainContent from "../../../layouts/maincontent/MainContent";
import LoadingSpinner from "../../../components/Loading/LoadingSpinner";
import UseAuth from "../../../hooks/UseAuth";
import { get } from "../../../adapters/xhr/api";
import styles from "../../pages/profile/UserSettings.module.css";
import { previewAndDownloadDeclerationFile } from "../../../adapters/apis/lookups/lookups";
import disputeInfo from "../../../assets/img/Icon feather-file-text.png";
import { PaginationTax } from "../../pagination/Pagination";

/* ─── Constants ─── */

const DEFAULT_PAGINATION = {
  current_page: 1,
  last_page: 1,
  per_page: 100,
  total: 0,
};

const TABS = [
  { key: "pdfs", label: "وثائق" },
  { key: "videos", label: "مقاطع فيديو" },
];

/* ─── Helpers (module-level, no React state needed) ─── */

const handlePreviewFile = async (url) => {
  try {
    const response = await previewAndDownloadDeclerationFile(url);

    const blob = new Blob([response?.data], {
      type: response.headers["content-type"],
    });

    const fileUrl = URL.createObjectURL(blob);
    window.open(fileUrl, "_blank");
  } catch (error) {
    if (error?.response?.status === 429) {
      alert("تعديت عدد المحاولات المسموح بها . يرجى الانتظار");
    }
  }
};

const handleDownloadFile = async (url) => {
  try {
    const response = await previewAndDownloadDeclerationFile(url);

    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    let fileName = "file";
    let originFileName = "file";
    const disposition = response.headers["content-disposition"];
    if (disposition) {
      const match = disposition.match(/filename="(.+?)"/);
      if (match) {
        originFileName = match[1];
      }
    }

    const mimeType = blob.type;
    const ext = originFileName.split(".").pop().toLowerCase();

    if (ext === "jfif" || mimeType === "image/jfif") {
      fileName = "file.jfif";
    } else if (mimeType === "image/jpeg" || mimeType === "image/jpg") {
      fileName = ext === "jpg" ? "file.jpg" : "file.jpeg";
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName.replace(/"/g, "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    if (error?.response?.status === 429) {
      alert("تعديت عدد المحاولات المسموح بها . يرجى الانتظار");
    }
  }
};

function getYoutubeEmbedUrl(rawUrl) {
  if (!rawUrl) return "";
  try {
    const url = new URL(rawUrl);
    if (url.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed${url.pathname}`;
    }
    const videoId = url.searchParams.get("v");
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.pathname.includes("/embed/")) {
      return rawUrl;
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

/* ─── Sub-components ─── */

const EmptyMessage = ({ message }) => (
  <div className="col-12 text-center text-muted py-4">
    <h6>{message}</h6>
  </div>
);

/* ─── Main Component ─── */

function HelpAndInstructions() {
  const { auth } = UseAuth();
  const isAuthenticated = Boolean(
    auth && auth.accessToken && auth.accessToken !== "",
  );
  const navigate = useNavigate();
  const [services, setServices] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState("pdfs");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfMaterials, setPdfMaterials] = useState([]);
  const [videoMaterials, setVideoMaterials] = useState([]);
  const [pdfPagination, setPdfPagination] = useState(DEFAULT_PAGINATION);
  const [videoPagination, setVideoPagination] = useState(DEFAULT_PAGINATION);
  const [limit, setLimit] = useState(100);

  const handleBack = useCallback(() => {
    navigate(isAuthenticated ? "/user-settings" : "/register");
  }, [isAuthenticated, navigate]);

  const getServices = useCallback(async () => {
    try {
      const response = await get("api/help-instructions/services");
      const servicesData = response?.data?.data || [];
      setServices(servicesData);

      setSelectedService((prevSelected) => {
        if (prevSelected && servicesData.some((s) => s.id === prevSelected)) {
          return prevSelected;
        }
        return servicesData.length > 0 ? servicesData[0].id : null;
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getHelpMaterials = useCallback(
    async (serviceId, page = 1, newLimit = limit) => {
      try {
        setIsLoading(true);
        setError(null);

        const [pdfResponse, videoResponse] = await Promise.all([
          get(`api/help-instructions/services/${serviceId}/contents/pdf`, {
            params: { page, per_page: newLimit },
          }),
          get(`api/help-instructions/services/${serviceId}/contents/video`, {
            params: { page, per_page: newLimit },
          }),
        ]);

        const pdfData = pdfResponse?.data?.data || {};
        const videoData = videoResponse?.data?.data || {};

        setPdfMaterials(pdfData.items || []);
        setVideoMaterials(videoData.items || []);
        setPdfPagination(
          pdfData.pagination || { ...DEFAULT_PAGINATION, per_page: newLimit },
        );
        setVideoPagination(
          videoData.pagination || { ...DEFAULT_PAGINATION, per_page: newLimit },
        );
        if (pdfData.pagination?.per_page || videoData.pagination?.per_page) {
          setLimit(
            pdfData.pagination?.per_page ||
              videoData.pagination?.per_page ||
              newLimit,
          );
        }
      } catch (err) {
        console.log(err);
        setError("حدث خطأ أثناء تحميل المواد الإرشادية");
      } finally {
        setIsLoading(false);
      }
    },
    [limit],
  );

  const handleMaterialPageChange = useCallback(
    async (type, page, newLimit = limit) => {
      if (!selectedService || !page || page < 1) return;

      try {
        setIsLoading(true);
        const endpoint = `api/help-instructions/services/${selectedService}/contents/${type}`;
        const response = await get(endpoint, {
          params: { page, per_page: newLimit },
        });
        const payload = response?.data?.data || {};

        if (type === "pdf") {
          setPdfMaterials(payload.items || []);
          setPdfPagination(
            payload.pagination || {
              ...DEFAULT_PAGINATION,
              per_page: newLimit,
            },
          );
        } else {
          setVideoMaterials(payload.items || []);
          setVideoPagination(
            payload.pagination || {
              ...DEFAULT_PAGINATION,
              per_page: newLimit,
            },
          );
        }
      } catch (err) {
        console.log(err);
        setError("حدث خطأ أثناء تغيير الصفحة");
      } finally {
        setIsLoading(false);
      }
    },
    [selectedService, limit],
  );

  const handleLimitChange = useCallback(
    (newLimit) => {
      setLimit(newLimit);
      const type = activeTab === "pdfs" ? "pdf" : "video";
      handleMaterialPageChange(type, 1, newLimit);
    },
    [activeTab, handleMaterialPageChange],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getServices();
  }, [getServices]);

  useEffect(() => {
    if (selectedService) {
      getHelpMaterials(selectedService);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  const activeServiceLabel = useMemo(
    () => services?.find((s) => s.id === selectedService)?.label,
    [services, selectedService],
  );

  const activePagination =
    activeTab === "pdfs" ? pdfPagination : videoPagination;
  const activeMaterialType = activeTab === "pdfs" ? "pdf" : "video";

  return (
    <MainContent>
      {services === null ? (
        <LoadingSpinner />
      ) : (
        <section className={styles.section}>
          <div
            className={`${styles.container} container`}
            style={{ marginTop: "-70px" }}
          >
            {/* HEADER */}
            <div className={styles.headerRow}>
              <button
                className={`${styles["btn-arrow"]} ms-3`}
                onClick={handleBack}
              >
                <i className="fa-solid fa-arrow-right mt-1"></i>
              </button>
              <h3 className="mb-0 ms-2 fw-bold">مساعدة و تعليمات</h3>
            </div>

            {/* SERVICES */}
            <div className={styles.servicesSection}>
              <h5 className={styles.sectionTitle}>الخدمات</h5>

              {services.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <h5>لا توجد خدمات متاحة</h5>
                </div>
              ) : (
              <>
                <div
                  className={styles.serviceList}
                  role="radiogroup"
                  aria-label="اختر الخدمة"
                >
                  {services.map((service) => {
                    const isSelected = selectedService === service.id;
                    return (
                      <label
                        key={service.id}
                        className={`${styles.serviceOption} ${isSelected ? styles.serviceOptionSelected : ""}`}
                      >
                        <input
                          type="radio"
                          name="service"
                          checked={isSelected}
                          onChange={() => setSelectedService(service.id)}
                        />
                        <span className={styles.serviceOptionLabel}>
                          <i className={`fa-solid ${service.icon}`}></i>
                          {service.name}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {/* Content for the selected service */}
                <div className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    <span>{activeServiceLabel}</span>
                  </div>

                  {/* TABS */}
                  <div className={`${styles.tabsRow} mt-4`}>
                    <div className={styles["tabs-wrapper"]}>
                      {TABS.map((tab) => (
                        <div
                          key={tab.key}
                          className={`${styles["tab-item"]} ${
                            activeTab === tab.key ? styles["active"] : ""
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                          role="tab"
                          aria-selected={activeTab === tab.key}
                          tabIndex={0}
                        >
                          <span className="fw-bold">{tab.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    {error && (
                      <div className="alert alert-danger text-center w-50 mx-auto">
                        <h6 className="mb-0">{error}</h6>
                      </div>
                    )}

                    {isLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        {/* PDFS TAB */}
                        {activeTab === "pdfs" && !error && (
                          <div className="row g-4">
                            {pdfMaterials?.length > 0 ? (
                              pdfMaterials.map((item) => (
                                <div
                                  className="file-item"
                                  key={
                                    item?.id || item?.title || item?.file_url
                                  }
                                >
                                  <div className="d-flex border border-2 rounded-3 shadow-sm bg-light py-2 px-3 justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-3">
                                      <img
                                        src={item?.image_url || disputeInfo}
                                        alt={item?.title || "PDF"}
                                        className="img-fluid"
                                        style={{
                                          width: 40,
                                          height: 40,
                                          objectFit: "cover",
                                        }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = disputeInfo;
                                        }}
                                      />
                                      <p className="mb-0" title={item?.title}>
                                        {item?.title}
                                      </p>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handlePreviewFile(item?.file_url)
                                        }
                                        className="border-0 bg-transparent"
                                      >
                                        <i
                                          className="fa-regular fa-eye fa-lg px-2"
                                          style={{ color: "#32A8ED" }}
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDownloadFile(item?.download_url)
                                        }
                                        className="border-0 bg-transparent"
                                      >
                                        <i
                                          className="fa-solid fa-cloud-arrow-down fa-lg px-2"
                                          style={{ color: "#32A8ED" }}
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <EmptyMessage message="لا توجد مواد توضيحية متاحة" />
                            )}
                          </div>
                        )}

                        {/* VIDEOS TAB */}
                        {activeTab === "videos" && !error && (
                          <div className="row g-4">
                            {videoMaterials?.length > 0 ? (
                              videoMaterials.map((item) => (
                                <div className="col-md-6" key={item.id}>
                                  <div className={styles.videoCard}>
                                    <div className="ratio ratio-16x9">
                                      <iframe
                                        width="560"
                                        height="315"
                                        src={getYoutubeEmbedUrl(item.video_url)}
                                        allowFullScreen
                                        title={item.title}
                                      ></iframe>
                                    </div>
                                    <h6 className="mt-3">{item.title}</h6>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <EmptyMessage message="لا توجد مواد توضيحية متاحة" />
                            )}
                          </div>
                        )}

                        {/* Pagination (shared across tabs) */}
                        {!error && activePagination.total > 0 && (
                          <PaginationTax
                            count={activePagination.total}
                            limit={limit}
                            active={activePagination.current_page || 1}
                            callback={(page) =>
                              handleMaterialPageChange(
                                activeMaterialType,
                                page,
                                limit,
                              )
                            }
                            handleLimitChange={handleLimitChange}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      )}
    </MainContent>
  );
}

export default HelpAndInstructions;
